import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { ShieldCheck, BarChart3, Share2, MessageSquare } from 'lucide-react';

const features = [
  { icon: MessageSquare, title: 'Agente Inteligente', desc: 'Consulta información sobre solicitudes, incidentes y acuerdos de nivel de servicio con lenguaje natural.' },
  { icon: BarChart3, title: 'Métricas de Uso', desc: 'Tablero de control con indicadores clave, tendencias y patrones de uso del sistema.' },
  { icon: Share2, title: 'Análisis Colaborativo', desc: 'Comparte sesiones de análisis con tu equipo y colabora en la toma de decisiones.' },
  { icon: ShieldCheck, title: 'Monitoreo ANS', desc: 'Supervisa el cumplimiento de Acuerdos de Niveles de Servicio e identifica infracciones.' },
];

export default function LoginPage() {
  const { login, isLoading } = useAuth();

  return (
    <div className="min-h-screen flex">
      {/* Panel izquierdo - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1B2A4A 0%, #0F1729 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full" style={{ background: 'radial-gradient(circle, #EDAA00 0%, transparent 70%)' }} />
          <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(circle, #2563EB 0%, transparent 70%)' }} />
        </div>

        <div className="relative z-10 flex flex-col justify-center px-16 py-12">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            <img
              src="/assets/logo-uniandes-vaf.png"
              alt="Universidad de los Andes - Vicerrectoría Administrativa y Financiera"
              className="h-20 object-contain brightness-0 invert"
            />
            <h1 className="text-4xl font-bold text-white mt-8 leading-tight">
              Asistente Inteligente de<br />
              <span style={{ color: '#EDAA00' }}>Apoyo Financiero</span>
            </h1>
            <p className="text-white/60 mt-4 text-lg max-w-md">
              Plataforma de análisis con IA para la Unidad de Matrículas y Apoyo Financiero.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 grid grid-cols-2 gap-4"
          >
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + i * 0.1 }}
                className="p-4 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm"
              >
                <f.icon className="w-5 h-5 mb-2" style={{ color: '#EDAA00' }} />
                <h3 className="text-white text-sm font-semibold">{f.title}</h3>
                <p className="text-white/50 text-xs mt-1 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Panel derecho - Login */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo móvil */}
          <div className="lg:hidden mb-8">
            <img
              src="/assets/logo-uniandes-vaf.png"
              alt="Universidad de los Andes - Vicerrectoría Administrativa y Financiera"
              className="h-16 object-contain"
            />
          </div>

          <div className="text-center mb-10">
            <div className="hidden lg:block mb-6">
              <img
                src="/assets/logo-uniandes-vaf.png"
                alt="Universidad de los Andes - Vicerrectoría Administrativa y Financiera"
                className="h-16 object-contain mx-auto"
              />
            </div>
            <h2 className="text-2xl font-bold" style={{ color: '#1B2A4A' }}>Iniciar Sesión</h2>
            <p className="text-gray-500 mt-2 text-sm">Accede con tu cuenta institucional</p>
          </div>

          <button
            onClick={login}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-semibold text-white transition-all duration-200 hover:shadow-lg disabled:opacity-70"
            style={{ backgroundColor: '#1B2A4A' }}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 23 23" fill="none">
                <path d="M11 0H0V11H11V0Z" fill="#F25022" />
                <path d="M23 0H12V11H23V0Z" fill="#7FBA00" />
                <path d="M11 12H0V23H11V12Z" fill="#00A4EF" />
                <path d="M23 12H12V23H23V12Z" fill="#FFB900" />
              </svg>
            )}
            {isLoading ? 'Autenticando...' : 'Iniciar sesión con @uniandes.edu.co'}
          </button>

          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              Universidad de los Andes | Vigilada Mineducación
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Unidad de Matrículas y Apoyo Financiero
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
